import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLang } from '../i18n';
import HScroll from './HScroll';
import { useViewContext } from '../viewContext';

const ProjectModal = ({ project, onClose }) => {
  const { t } = useLang();
  const { setView } = useViewContext();
  const [lightbox, setLightbox] = useState(null); // index into gallery or null
  const gallery = project?.gallery || [];

  const closeLightbox = useCallback(() => setLightbox(null), []);
  const prev = useCallback(() => setLightbox((i) => (i === null ? i : (i - 1 + gallery.length) % gallery.length)), [gallery.length]);
  const next = useCallback(() => setLightbox((i) => (i === null ? i : (i + 1) % gallery.length)), [gallery.length]);

  useEffect(() => {
    if (!project) return;
    const onKey = (e) => {
      if (lightbox !== null) {
        if (e.key === 'Escape') closeLightbox();
        if (e.key === 'ArrowLeft') prev();
        if (e.key === 'ArrowRight') next();
      } else if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [project, lightbox, onClose, closeLightbox, prev, next]);

  // Reset lightbox when project changes / closes
  useEffect(() => { setLightbox(null); }, [project]);

  // Report the current project / open photo to Dom.
  useEffect(() => {
    if (!project) { setView(''); return; }
    if (lightbox !== null && gallery[lightbox]) {
      const item = gallery[lightbox];
      const cap = typeof item === 'object' && item.caption ? ` The photo caption is: "${item.caption}".` : '';
      setView(`The visitor has opened the "${project.title}" project case file and is viewing an enlarged photo (image ${lightbox + 1} of ${gallery.length}) from its gallery.${cap}`);
    } else {
      setView(`The visitor is reading the "${project.title}" project case file.`);
    }
    return () => setView('');
  }, [project, lightbox, gallery, setView]);

  const scopeTags = project?.scope ? project.scope.split('|').map((s) => s.trim()).filter(Boolean) : [];

  const MetaRow = ({ label, value }) => {
    if (!value) return null;
    return (
      <div className="py-4 border-b border-white/10">
        <div className="label-mono text-white/40 mb-1.5">{label}</div>
        <div className="font-body text-white/85 text-sm md:text-base leading-relaxed">{value}</div>
      </div>
    );
  };

  return (
    <AnimatePresence>
      {project && (
        <motion.div
          data-testid="project-modal"
          className="fixed inset-0 z-[90] flex items-start md:items-center justify-center p-0 md:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          <div className="absolute inset-0 bg-black/85 backdrop-blur-md" onClick={onClose} data-testid="project-modal-backdrop" />

          <motion.div
            className={`relative w-full md:max-w-5xl max-h-full md:max-h-[90vh] overflow-y-auto bg-[#0a1220] md:rounded-2xl border border-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.6)] transition-[filter] duration-300 ${lightbox !== null ? 'blur-[6px]' : ''}`}
            initial={{ y: 40, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 20, opacity: 0, scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 320, damping: 32 }}
          >
            {/* Hero image — large, fading into the panel at the bottom */}
            <div className="relative h-72 sm:h-96 md:h-[460px] overflow-hidden md:rounded-t-2xl">
              <img src={project.img} alt={project.title} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a1220] via-[#0a1220]/55 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#0a1220] to-transparent" />
              <button
                data-testid="project-modal-close"
                onClick={onClose}
                className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/50 hover:bg-ember border border-white/20 flex items-center justify-center text-white transition-colors"
                aria-label="Close"
              >
                <X size={20} />
              </button>
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                <div className="label-mono text-ember mb-3">{project.no} · {project.region}</div>
                <h2 className="font-display font-light text-white text-2xl sm:text-3xl md:text-4xl leading-[1.1] tracking-tight">
                  {project.title}
                </h2>
              </div>
            </div>

            {/* Body */}
            <div className="p-6 md:p-8">
              {/* Photo strip — directly under the title */}
              {gallery.length > 0 && (
                <div className="mb-8">
                  <div className="label-mono text-white/40 mb-4">{t('proj.gallery')}</div>
                  <HScroll className="pb-2" data-testid="project-gallery-strip" arrows>
                    {gallery.map((item, i) => {
                      const src = typeof item === 'string' ? item : item.src;
                      const cap = typeof item === 'string' ? '' : (item.caption || '');
                      return (
                      <button
                        key={i}
                        data-testid={`gallery-thumb-${i}`}
                        onClick={() => setLightbox(i)}
                        className="group relative shrink-0 w-36 sm:w-44 aspect-[16/10] rounded-md overflow-hidden border border-white/10 hover:border-ember/60 transition-colors"
                      >
                        <img src={src} alt={cap || `${project.title} ${i + 1}`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" draggable="false" />
                        <div className="absolute inset-0 bg-navy/10 group-hover:bg-navy/0 transition-colors" />
                        {cap && (
                          <>
                            <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-navy via-navy/70 to-transparent" />
                            <span data-testid={`gallery-caption-${i}`} className="absolute bottom-2 left-2 right-2 label-mono text-white/90 text-[8px] leading-tight line-clamp-2 text-left drop-shadow">{cap}</span>
                          </>
                        )}
                      </button>
                      );
                    })}
                  </HScroll>
                </div>
              )}

              {scopeTags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-8">
                  {scopeTags.map((tag, i) => (
                    <span key={i} data-testid={`scope-chip-${i}`} className="label-mono text-white/80 px-3 py-1.5 rounded-full border border-white/15 bg-white/[0.04]">
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              <div className="grid md:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-8 md:gap-12">
                <div>
                  <MetaRow label={t('proj.year')} value={project.year} />
                  <MetaRow label={t('proj.location')} value={project.location} />
                  <MetaRow label={t('proj.project')} value={project.project} />
                  <MetaRow label={t('proj.vessel')} value={project.vessel} />
                  <MetaRow label={t('proj.cargo')} value={project.cargo} />
                  <MetaRow label={t('proj.codes')} value={project.codes} />
                  {project.metric && (
                    <div className="pt-5">
                      <div className="font-display font-bold text-ember text-3xl leading-none">{project.metric}</div>
                      <div className="label-mono text-white/40 mt-2">{project.metricLabel}</div>
                    </div>
                  )}
                </div>

                {project.facts && project.facts.length > 0 && (
                  <div>
                    <div className="label-mono text-white/40 mb-5">{t('proj.factsTitle')}</div>
                    <ul className="space-y-4">
                      {project.facts.map((f, i) => (
                        <li key={i} data-testid={`fact-${i}`} className="flex gap-3 items-start">
                          <span className="mt-1 shrink-0 w-5 h-5 rounded-full bg-ember/15 border border-ember/40 flex items-center justify-center">
                            <Check size={12} className="text-ember" />
                          </span>
                          <span className="font-body text-white/80 text-sm md:text-base leading-relaxed">{f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </motion.div>

          {/* Lightbox */}
          <AnimatePresence>
            {lightbox !== null && (
              <motion.div
                data-testid="project-lightbox"
                className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-10"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                onClick={closeLightbox}
              >
                <div className="absolute inset-0 bg-black/80" />
                <button
                  data-testid="lightbox-close"
                  onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
                  className="absolute top-5 right-5 z-10 w-11 h-11 rounded-full bg-black/60 hover:bg-ember border border-white/20 flex items-center justify-center text-white"
                  aria-label="Close photo"
                >
                  <X size={22} />
                </button>
                {gallery.length > 1 && (
                  <>
                    <button
                      data-testid="lightbox-prev"
                      onClick={(e) => { e.stopPropagation(); prev(); }}
                      className="absolute left-3 sm:left-6 z-10 w-12 h-12 rounded-full bg-black/60 hover:bg-ember border border-white/20 flex items-center justify-center text-white"
                      aria-label="Previous"
                    >
                      <ChevronLeft size={26} />
                    </button>
                    <button
                      data-testid="lightbox-next"
                      onClick={(e) => { e.stopPropagation(); next(); }}
                      className="absolute right-3 sm:right-6 z-10 w-12 h-12 rounded-full bg-black/60 hover:bg-ember border border-white/20 flex items-center justify-center text-white"
                      aria-label="Next"
                    >
                      <ChevronRight size={26} />
                    </button>
                  </>
                )}
                <motion.img
                  key={lightbox}
                  src={typeof gallery[lightbox] === 'string' ? gallery[lightbox] : gallery[lightbox].src}
                  alt={`${project.title} ${lightbox + 1}`}
                  className="relative z-[5] max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                  initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.2 }}
                  onClick={(e) => e.stopPropagation()}
                  draggable="false"
                />
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-1.5 text-center px-4">
                  {(typeof gallery[lightbox] === 'object' && gallery[lightbox].caption) && (
                    <span className="font-body text-white/90 text-sm md:text-base max-w-xl leading-snug">{gallery[lightbox].caption}</span>
                  )}
                  <span className="label-mono text-white/60">{lightbox + 1} / {gallery.length}</span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ProjectModal;
